/**
 * The About page — the words, the people, and nothing else.
 *
 * EVERY WORD HERE TRACES TO yedding-brain/BRAND-CONTEXT.md
 *   Each block carries the section it came from in a comment above it. A
 *   sentence with no source section does not belong in this file. The two
 *   founder records come from §8.2, which is the complete list of what may
 *   be published about either person. Nothing outside §8.2 goes in a
 *   founder record, ever — not a home locality, not a birth date, not a
 *   personal email address.
 *
 * NO EM DASH, ANYWHERE
 *   yedding-brain/BRAND-CONTEXT.md §12, decided 2026-09-21. An em dash in
 *   marketing prose is the clearest tell that a machine wrote it, and a
 *   page whose whole job is trust cannot afford to read as generated. Use
 *   a colon, a full stop, or a comma. scripts/check-about.js fails the
 *   build if one appears in a published string.
 *
 * THE ONLY NUMBERS ALLOWED
 *   Dates, award placements and the phone number. **No price, no fee, no
 *   percentage, no deposit, no guest count.** The approved 80/20 host
 *   split is not on this page either; it belongs on /how-it-works, which
 *   is task 11b.
 *
 * PHOTOS ARRIVE LATER, WITH NO CODE CHANGE
 *   Each founder record takes an optional `photo`. Leave it out and the
 *   card renders as a clean text card. Add `{ src, alt }` and the card
 *   grows an image and the Person schema grows an `image` field. Both
 *   sides read the same record, so they cannot disagree. See
 *   docs/aeo/AEO-LEDGER.md, Improvement notes, 2026-09-21.
 *
 * ONE COPY OF THE WORDS
 *   src/components/About builds both the visible page and the AboutPage
 *   and Person JSON-LD from this file. There is one copy of every
 *   sentence, so the page and the structured data cannot drift apart.
 *
 * See docs/aeo/AEO-LEDGER.md, task 11a.
 */

/** Cleaned URL. §8.1: never a share link, never a tracking parameter. */
const FOUNDER_INSTAGRAM = 'https://www.instagram.com/yedding.acquainted';
const COMPANY_INSTAGRAM = 'https://www.instagram.com/yeddingllc';

/**
 * The two people. §8.2 is the complete, approved list.
 *
 * `id` becomes the schema @id. `#brenna` already exists in the
 * Organization graph in public/index.html and must not change.
 */
export const founders = [
  {
    id: 'brenna',
    name: 'Brenna Wilkinson Johnson',
    jobTitle: 'CEO and Founder',
    // Source: BRAND-CONTEXT.md §8 (role, April 2021) and §8.2 (awards,
    // certification, speaking). Nothing here is outside that list.
    bio:
      'Brenna founded Yedding in April 2021 and runs the company as CEO. She has since led Yedding to three startup awards: Top 7 Finalist at EO GSEA Utah 2021, a tie for second place out of 42 teams at the Big Idea Competition, a BYU-sponsored startup competition, and Fan Favorite at RevRoad’s 2021 Entrepreneurship Competition. Brenna also holds an AI Business Trainer certification from Real AI Dynamics, earned over three days of intensive training, and in 2024 she presented on the application of AI in business at the University of Utah and at the Utah State Capitol Building.',
    sameAs: FOUNDER_INSTAGRAM
    // photo: { src: '/founder-brenna.jpg', alt: '...' }  <- see the header
  },
  {
    id: 'braeden',
    name: 'Braeden Johnson',
    jobTitle: 'Head of Operations',
    // Source: BRAND-CONTEXT.md §8 (role) and §8.2 (degree, AI work, the
    // association, the City of Orem class, the certification). The first
    // sentence is Braeden's own wording, supplied 2026-09-21.
    bio:
      'Braeden runs the behind-the-scenes work that keeps Yedding moving: the website, the automations behind it, and the processes that make a booking run smoothly. He holds a degree in Economics from Brigham Young University and has worked heavily with AI since 2023. Braeden co-founded the AI in Business academic association through the Marriott School of Business, taught an AI class for professionals in partnership with the City of Orem, and holds an AI Business Trainer certification from Real AI Dynamics, an IT consulting firm.',
    sameAs: FOUNDER_INSTAGRAM
  }
];

const about = {
  title: 'About Yedding',

  // Source: §1 (what Yedding is), §8 (April 2021, Salt Lake City), and the
  // 2026-09-21 decision that the marriage is published as a company fact
  // and never as an origin story about who joined when.
  intro:
    'Yedding is a marketplace for finding and booking distinctive event venues by the day. It was founded in April 2021 and is based in Salt Lake City, Utah. It is owned and operated by a married couple, Brenna Wilkinson Johnson and Braeden Johnson.',

  sections: [
    {
      id: 'what-yedding-is',
      heading: 'What Yedding is',
      // Source: §1, §3 (say "generally", Yedding is an open platform) and
      // §7 (every venue is personally reviewed; never list what is checked).
      paragraphs: [
        'Yedding lists distinctive, non-traditional event venues that you can rent by the day. Listings are generally private estates, ranches, mountain properties and other characterful places rather than commercial event halls. Every venue is personally reviewed and approved before it goes on the site.',
        'Yedding is not a wedding planner, not an all-inclusive venue, and not a vacation rental site. It is where you find the venue. What happens inside it is yours to decide.'
      ]
    },
    {
      id: 'why-yedding-exists',
      heading: 'Why Yedding exists',
      // Source: §2.1 to §2.4, the four problems, in that order. No price
      // figure and no percentage: §7 and §2.4.
      paragraphs: ['A traditional venue makes four things hard. Yedding was built to fix all four.'],
      points: [
        {
          id: 'vendors',
          lead: 'You cannot choose your own vendors.',
          text: 'Most venues hand you an approved list. Every venue on Yedding lets you hire any caterer, florist or photographer, or any other vendor you want. This is a hard platform rule, not a preference. A venue that requires its own vendor list is not accepted onto Yedding.'
        },
        {
          id: 'eight-hours',
          lead: 'You get eight hours.',
          text: 'Setup and cleanup eat into the event itself. Many Yedding venues are booked for the whole day instead.'
        },
        {
          id: 'hidden-fees',
          lead: 'The quote hides the real cost.',
          text: 'Yedding shows every charge before you book.'
        },
        {
          id: 'price',
          lead: 'Commercial event space is expensive.',
          text: 'A non-traditional property costs significantly less, for a comparable or better place.'
        }
      ]
    },
    {
      id: 'what-you-are-renting',
      heading: 'What you are renting',
      // Source: §4, Braeden's approved framing. The first two sentences are
      // his words. The full answer lives on /faq, so this page links there
      // instead of repeating the whole paragraph: the same block on two
      // pages is duplicate content.
      paragraphs: [
        'A Yedding venue is a blank canvas, and that freedom is the point. The listed price is for the event space itself, so the flowers, the catering and the planning stay your choice.'
      ],
      outro: {
        before: 'If you want an all-inclusive package where the venue handles everything, ',
        link: { href: '/faq#all-inclusive', text: 'read this answer first' },
        after: '. Yedding may not be the right fit.'
      }
    },
    {
      id: 'the-people',
      heading: 'The people behind Yedding',
      // Source: §8 (ownership), §8.2 (the AI sentence), and the 2026-09-21
      // decision on how the marriage is framed. The AI assistant is stated
      // in the future tense on purpose: it does not exist yet.
      paragraphs: [
        'Yedding is owned and operated by a married couple. It is not a brand name over an anonymous team. If you email Yedding, one of them reads it (or to most quickly serve you, basic questions will be answered by their AI assistant).',
        'Both have worked closely with AI since 2023. It is part of how two people can personally review every venue and still list them for far less than commercial event space.'
      ],
      founders
    },
    {
      id: 'behind-the-scenes',
      heading: 'Behind the scenes',
      // Source: §8.1. The founder account goes on the people, never on
      // Organization. Linking it here and from both Person nodes is what
      // tells an engine the account and the founders are the same entity.
      instagram: {
        before: 'Brenna and Braeden run an Instagram account about building Yedding: ',
        link: { href: FOUNDER_INSTAGRAM, text: '@yedding.acquainted' },
        after: '.'
      },
      company: {
        before: 'It is the two of them, not the company. The venues are on ',
        link: { href: COMPANY_INSTAGRAM, text: '@yeddingllc' },
        after: '. This one is the work behind them.'
      }
    },
    {
      id: 'where-yedding-operates',
      heading: 'Where Yedding operates',
      // Source: §8 (Salt Lake City, region level only, never a street) and
      // §9 (Utah is the core market; Las Vegas has one listing). Denver is
      // deliberately absent: §9 permits it only as "coming soon", and a
      // promise with a date attached does not belong on a trust page.
      outro: {
        before: 'Yedding, LLC is based in Salt Lake City, Utah. Most venues are in Utah, and there is a listing in Las Vegas, Nevada. ',
        link: { href: '/search/', text: 'Search the venues available today' },
        after: '.'
      }
    },
    {
      id: 'contact',
      heading: 'Contact',
      // Source: §8. support@yedding.com and the Google Business Profile
      // number, which must match everywhere it appears.
      contact: {
        email: 'support@yedding.com',
        phone: '(801) 923-4914',
        phoneHref: '+18019234914',
        after: ' We’d love to hear from you.'
      }
    }
  ]
};

/** Every visible sentence on the page, in order. Used by scripts/check-about.js. */
export const allProse = () => {
  const out = [about.intro];
  about.sections.forEach((section) => {
    out.push(section.heading);
    (section.paragraphs || []).forEach((p) => out.push(p));
    (section.points || []).forEach((p) => out.push(`${p.lead} ${p.text}`));
    (section.founders || []).forEach((f) => out.push(f.name, f.jobTitle, f.bio));
    [section.outro, section.instagram, section.company].forEach((b) => {
      if (b) out.push(`${b.before}${b.link.text}${b.after}`);
    });
    if (section.contact) out.push(`Email ${section.contact.email} or call ${section.contact.phone}.${section.contact.after}`);
  });
  return out;
};

export default about;
